import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './keycloak';
import { makeAuthenticatedRequest } from './api/axiosConfig';

const ProxyHandlerSimple = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, keycloak, isAuthenticated, isLoading } = useAuth();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  const bucket = searchParams.get('bucket');
  const key = searchParams.get('key');

  // Función para iniciar el proceso de descarga
  const startDownload = async () => {
    try {
      console.log('📥 Iniciando descarga de archivo');
      setStatus('fetching');
      
      const apiUrl = import.meta.env.VITE_API_GATEWAY;
      console.log('🔗 URL API:', `${apiUrl}?action=proxy&key=${encodeURIComponent(key)}&bucket=${encodeURIComponent(bucket)}`);
      
      const response = await makeAuthenticatedRequest({
        method: 'GET',
        url: `${apiUrl}?action=proxy&key=${encodeURIComponent(key)}&bucket=${encodeURIComponent(bucket)}`,
        headers: {
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      }, token);

      console.log('✅ Respuesta recibida:', response);

      // Crear URL de objeto para el archivo
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      
      // Obtener el nombre del archivo para la descarga
      const filename = key.split('/').pop() || 'archivo';
      
      // Crear un enlace temporal para descargar el archivo
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar la URL del objeto
      window.URL.revokeObjectURL(url);
      
      setStatus('success');
      console.log('✅ Descarga completada');
      
      // Cerrar ventana después de un breve delay
      setTimeout(() => {
        window.close();
      }, 1000);

    } catch (err) {
      console.error('❌ Error en descarga:', err);
      setStatus('error');
      
      if (err.response?.status === 401) {
        setError('Token expirado. Por favor, inicia sesión nuevamente.');
      } else if (err.response?.status === 403) {
        setError('No tienes permisos para acceder a este archivo');
      } else if (err.response?.status === 404) {
        setError('Archivo no encontrado');
      } else {
        setError(`Error al descargar el archivo: ${err.message}`);
      }
    }
  };

  // Función para manejar la autenticación
  const handleAuth = () => {
    console.log('🔐 Iniciando proceso de autenticación');
    setStatus('authenticating');
    
    keycloak.login({
      redirectUri: window.location.href
    }).catch(err => {
      console.error('❌ Error en autenticación:', err);
      setStatus('error');
      setError('Error de autenticación');
    });
  };

  // Effect principal - se ejecuta solo cuando cambian los estados críticos
  useEffect(() => {
    console.log('🔍 ProxyHandler: Estado actual:', {
      bucket: !!bucket,
      key: !!key,
      isLoading,
      isAuthenticated,
      user: !!user,
      token: !!token,
      status
    });

    // Verificar parámetros
    if (!bucket || !key) {
      setStatus('error');
      setError('Parámetros requeridos: bucket y key');
      return;
    }

    // Si está cargando, esperar
    if (isLoading) {
      setStatus('loading');
      return;
    }

    // Si no está autenticado y no está en proceso, iniciar autenticación
    if (!isAuthenticated && status === 'loading') {
      handleAuth();
      return;
    }

    // Si está autenticado y tiene todos los datos, iniciar descarga
    if (isAuthenticated && user && token && status === 'loading') {
      startDownload();
      return;
    }

  }, [bucket, key, isLoading, isAuthenticated, user, token]); // Solo dependencias críticas

  const getStatusMessage = () => {
    switch (status) {
      case 'loading':
        return 'Inicializando...';
      case 'authenticating':
        return 'Redirigiendo a autenticación...';
      case 'fetching':
        return 'Descargando archivo...';
      case 'success':
        return 'Archivo descargado correctamente.';
      case 'error':
        return error || 'Error desconocido';
      default:
        return 'Procesando...';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
      case 'authenticating':
      case 'fetching':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '🔄';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="text-6xl mb-4">{getStatusIcon()}</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Proxy Seguro de Archivos
          </h1>
          <p className="text-gray-600 mb-6">
            {getStatusMessage()}
          </p>

          {status === 'error' && (
            <div className="space-y-4">
              {error?.includes('autenticación') || error?.includes('Token expirado') ? (
                <button
                  onClick={handleAuth}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Iniciar sesión
                </button>
              ) : (
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Reintentar
                </button>
              )}
            </div>
          )}

          {(status === 'loading' || status === 'fetching') && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
            </div>
          )}

          {status === 'authenticating' && (
            <div className="text-sm text-gray-500 mt-4">
              Si no se abre automáticamente, haz clic para{' '}
              <button
                onClick={handleAuth}
                className="text-blue-500 hover:text-blue-600 underline"
              >
                iniciar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProxyHandlerSimple;
